// Angular imports
import { Injectable } from '@angular/core';

// Print-js
import printJS from 'print-js';
import { saveAs } from 'file-saver';

// Import services
import { LoaderService } from '../loader/loader.service';
import { MessageService, MessageType } from '../services/message.service';
import { DataService, ExportType } from '../services/data.service';

// Import utils
import { ExcelUtils } from '../utils';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { CalculationMetaApi } from '../types';
import { Store } from '@ngrx/store';
import { AppState } from '../state-management/app-state';
import { selectAggregation, selectCalculationMeta, selectViewlines, selectWarnings } from '../state-management/calculation-selectors';
import { Aggregation, ViewModelLine, WarningType } from '../state-management/calculation-state';

// Define constants
const NAME_MAP: { [type in ExportType]: string } = {
    [ExportType.budget]: 'Open begroting',
    [ExportType.hours]: 'Externe Uren',
    [ExportType.projects]: 'Projects begroting',
    [ExportType.quotation]: 'Externe begroting',
    [ExportType.smeulders]: 'Smeulders begroting',
    [ExportType.net]: 'Netto zonder opslagen',
    [ExportType.gross]: 'Externe open begroting',
    [ExportType.quantities]: 'Hoeveelheden',
    [ExportType.pga]: 'PGA begroting',
    [ExportType.mmw]: 'Meer-/minderwerk specificatie'
}

@Injectable({
    providedIn: 'root'
})
export class ExportService {
    private unsubscribe$ = new Subject<void>();

    public meta$ = new BehaviorSubject<CalculationMetaApi>(undefined);
    private lines: ViewModelLine[] = [];
    private totals: Aggregation = undefined;
    private warnings: WarningType[] = [];

    constructor(
        private loader: LoaderService,
        private messageService: MessageService,
        private dataService: DataService,
        // private applicationDataService: ApplicationDataService,
        private store: Store<AppState>
    ) {
        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => {
            this.meta$.next(meta);
        })

        this.store.select(selectViewlines).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(lines => {
            this.lines = lines;
        })

        this.store.select(selectAggregation).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(agg => {
            this.totals = agg;
        })

        this.store.select(selectWarnings).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((warnings) => {
            this.warnings = warnings;
        })
    }

    public ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public async exportCuf() {
        if (!(await this.checkExportWarnings())) return;

        await this.loader.performAsyncTask(async () => {
            if (!this.meta$.value?.projectId || !this.meta$.value?.id) {
                return;
            }

            // Export as cuf
            const xml = await this.dataService.exportCalculationAsCuf(this.meta$.value.projectId, this.meta$.value.id, this.meta$.value.currentVersion ?? 0);

            saveAs(new Blob([xml], { type: 'text/xml' }), `${this.meta$.value.description}.xml`);
        });
    }

    public async exportExcel(): Promise<void> {
        if (!(await this.checkExportWarnings())) return;

        await this.loader.performAsyncTask(async () => {
            // // Get the active project
            // const project = this.applicationDataService.activeProject;

            // // Get the active calculation
            // const calculation = this.applicationDataService.activeCalculation;

            // Convert calculation
            const workbook = ExcelUtils.convertCalculation(this.lines, this.totals, this.meta$.value.percentages);

            // Write to buffer
            const buffer = new Uint8Array(await workbook.xlsx.writeBuffer());

            // Save file
            saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${this.meta$.value.description}.xlsx`);
        });
    }

    public async exportCostExcel(): Promise<void> {
        if (!(await this.checkExportWarnings())) return;

        await this.loader.performAsyncTask(async () => {
            // Get the budget check from data service
            const invoices = await this.dataService.getBudgetCheck(this.meta$.value.projectId, this.meta$.value.id);

            // Convert calculation
            const workbook = ExcelUtils.exportCostManagement(this.lines, invoices);

            // Write to buffer
            const buffer = new Uint8Array(await workbook.xlsx.writeBuffer());

            // Save file
            saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${this.meta$.value.projectId} kostenbewaking.xlsx`);
        });
    }

    public async exportPdf(type: ExportType) {
        if (!(await this.checkExportWarnings())) return;

        await this.loader.performAsyncTask(async () => {
            // // Get the active project
            // const project = this.applicationDataService.activeProject;

            // // Get the active calculation
            // const calculation = this.applicationDataService.activeCalculation;

            // Get the pdf blob
            const blob = await this.dataService.exportCalculationAsPdf(this.meta$.value.projectId, this.meta$.value.id, this.meta$.value.currentVersion ?? 0, type, this.meta$.value.includeTerms);

            // Get base64 from blob
            const base64 = await this.blobToBase64(blob);

            try {
                // Print
                printJS({ printable: base64, base64: true, type: 'pdf', documentTitle: `${this.meta$.value.projectId} ${this.meta$.value.description} v${this.meta$.value.currentVersion} - ${NAME_MAP[type]}.pdf` });
            } catch (err) {
                console.error(err);
            }
        });
    }

    /* --- Helpers --- */

    private async checkExportWarnings(): Promise<boolean> {
        // // Get the active calculation
        // const calculation = this.applicationDataService.activeCalculation;

        // Check if the calculation has warnings
        const hasWarnings = this.warnings.length > 0;

        if (hasWarnings) {
            return new Promise((resolve) => {
                this.messageService.show({
                    title: 'Waarschuwing',
                    message: 'Let op, er zijn waarschuwing in je calculatie, weet je zeker dat je wil exporteren?',
                    type: MessageType.Warning,
                    buttons: [
                        {
                            text: 'Exporteren',
                            handler: () => resolve(true)
                        },
                        {
                            text: 'Nee',
                            handler: () => resolve(false)
                        }
                    ]
                });
            });
        } else {
            return true;
        }
    }

    private blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => {
                const base64 = (reader.result as string).replace(/data:application\/.+;base64,/gi, '');
                resolve(base64);
            }
            reader.onerror = reject;

            reader.readAsDataURL(blob);
        });
    }
}
